import {
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
  Switch,
  Text,
  Flex,
  CheckboxGroup,
} from '@chakra-ui/react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  EntityTag,
  CreateTagRequest,
  tagsApi,
} from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from '../shared/alert/ErrorDetails';
import { SubmitButton } from '../shared/form/SubmitButton';
import { TexasFormLabel } from '../shared/form/TexasFormLabel';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SharedDisclosureProps } from '../shared/types';
import { RoleRestrict } from '../shared/RoleRestrict';
import { Role } from '@texas/api/endpoints/userApi';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { EntityTypeCheckboxes } from './EntityTypeCheckboxes';

export interface CreateTagModalProps extends SharedDisclosureProps {
  onTagCreated: (tag: EntityTag) => void;
}

const defaultValues: CreateTagRequest = {
  label: '',
  color: '#3182ce',
  systemTag: false,
  isShared: false,
  applicableEntityTypes: null,
};

export function CreateTagModal({
  isOpen,
  onClose,
  onTagCreated,
}: CreateTagModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateTagRequest>({
    defaultValues: defaultValues,
  });

  const labelValue = useWatch({
    control,
    name: 'label',
    defaultValue: '',
  });

  const isShared = useWatch({
    control,
    name: 'isShared',
    defaultValue: false,
  });

  const { t } = useTranslation();
  const toast = useToast();

  const {
    request: createTagRequest,
    loading,
    error,
  } = useApiRequest(tagsApi.create);

  const onSubmit = async (data: CreateTagRequest) =>
    await request(createTagRequest, [data], (tag: EntityTag) => {
      toast({
        title: t('general.created'),
        status: 'success',
        isClosable: true,
      });
      reset(defaultValues);
      onTagCreated(tag);
      onClose();
    });

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('tags.createNew')}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            <VStack spacing={4}>
              <FormControl isRequired={true} isInvalid={!!errors.label}>
                <TexasFormLabel>{t('tags.label')}</TexasFormLabel>
                <Input
                  variant="outline"
                  {...register('label', {
                    required: true,
                    maxLength: {
                      value: 100,
                      message: t('errors.maxLength', { count: 100 }),
                    },
                  })}
                  placeholder={t('tags.label')}
                  autoFocus={true}
                />
                <FormCounter length={labelValue.length} maxLength={100} />
                <ErrorLabel text={errors.label?.message} />
              </FormControl>
              <FormControl isRequired={true} isInvalid={!!errors.color}>
                <TexasFormLabel>{t('tags.color')}</TexasFormLabel>
                <Input
                  type="color"
                  {...register('color', {
                    required: true,
                  })}
                  height={10}
                  padding={1}
                  cursor="pointer"
                />
                <ErrorLabel text={errors.color?.message} />
              </FormControl>
              <RoleRestrict allow={[Role.SystemAdministrator]}>
                <FormControl>
                  <Flex justifyContent="space-between">
                    <Text>{t('tags.isShared')}</Text>
                    <Switch {...register('isShared')} />
                  </Flex>
                </FormControl>
                {isShared && (
                  <FormControl>
                    <TexasFormLabel>
                      {t('tags.applicableEntityTypes')}
                    </TexasFormLabel>
                    <Text fontSize="sm" color="gray.400" mb={2}>
                      {t('tags.applicableEntityTypesDescription')}
                    </Text>
                    <Controller
                      name="applicableEntityTypes"
                      control={control}
                      render={({ field }) => (
                        <CheckboxGroup
                          value={field.value?.map(String) ?? []}
                          onChange={(values) => {
                            const entityTypes =
                              values.length > 0
                                ? values.map((v) => Number(v) as EntityType)
                                : null;
                            field.onChange(entityTypes);
                          }}
                        >
                          <EntityTypeCheckboxes />
                        </CheckboxGroup>
                      )}
                    />
                  </FormControl>
                )}
              </RoleRestrict>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <SubmitButton loading={loading}>{t('general.create')}</SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
